import { A3C_LOAD_BEHAVIOR_DISABLED } from 'src/commons/constants';
import { isChatDisabledUrl } from 'src/commons/utils/a3cChatUrlRules';

/**
 * The default A3C agent injected under the A3C_EVERYWHERE weblab family when
 * the page did not provide one. Exported so all surfaces
 * (EmbeddedChatManager panel, sidenav Ads Agent button, focus-header Ads
 * Agent toggle) resolve the same default and cannot drift.
 */
export const A3C_EVERYWHERE_DEFAULT_AGENT = 'ads_agent_orchestrator';

/**
 * The only part of `chromeConfig.a3cConfig` the gate needs. Kept structural
 * (rather than importing the slice type) so the gate stays a leaf module with
 * no dependency on the Redux slice. `selectedAgentIdentifier` is typed to
 * allow null because JSON serializers emit null for unset fields (Mons/Sauron
 * configs), and the resolution logic treats null as "not set".
 */
interface A3cChatGateConfig {
  loadBehavior?: string;
  embeddedChatConfig?: { selectedAgentIdentifier?: string | null };
  flyoverChatConfig?: { selectedAgentIdentifier?: string | null };
}

/**
 * Whether the AAO-owned unified chat gate explicitly vetoes the A3C
 * EVERYWHERE experience on this page.
 *
 * AdvertisingConsoleTrimService resolves the FeatureFlagService flag
 * `isAdsAgentEnabled` — a composite of AAO's weblab and AAO's customer-level
 * Bullseye segment — once per page load in HeaderController and writes the
 * boolean from headerView.jsp, so the value is available synchronously on first
 * render.
 *
 * Scope: the veto applies ONLY to the everywhere default agent (the
 * A3C_EVERYWHERE weblab family, see useA3cEverywhereEnabled). It is NOT a
 * page-level chat kill switch and is deliberately not part of
 * {@link isA3cChatDisabled}: legacy agents that pages wire themselves through
 * `embeddedChatConfig.selectedAgentIdentifier` have not migrated onto AAO's
 * flag, so vetoing them here hid chat on those pages for customers the flag
 * was never meant to cover. Pages that own their agent keep it regardless of
 * the gate; only the everywhere default is withheld.
 *
 * The check is `=== false`, i.e. fail OPEN: only an explicit `false` vetoes.
 * That is deliberate and differs from `isUnifiedA3CChatEnabled()` in
 * AdConsoleDashboardApp (src/hooks/useA3cAgent.ts), which fails closed on an
 * `=== true` check. The two behave identically on every Trim-rendered page,
 * because headerView.jsp always emits an explicit `true`/`false` literal — even
 * for a missing model attribute, since EL evaluates a null attribute compared
 * against true as false. They diverge only where the global is never set at all:
 * non-Trim hosts (BTSW, standalone) and older Trim bundles. Failing open there
 * keeps those surfaces on their pre-existing weblab-only behavior instead of
 * silently losing chat.
 *
 * Note this is NOT SSR-safe, matching the rest of ChromeUI: the module reads
 * `window` directly, as `isChatDisabledUrl(window.location.href)` already does.
 */
export function isUnifiedA3CChatVetoed(): boolean {
  return typeof window !== 'undefined' && window.isA3CEnabled === false;
}

/**
 * Single source of truth for "A3C is fully off on this page": no container, no
 * agent, no config injection, no Ads Agent toggle — even when an
 * A3C_EVERYWHERE weblab or a page-provided agent would otherwise show chat.
 *
 * Two independent page-level vetoes:
 * - the page opted out via `a3cConfig.loadBehavior = DISABLED`
 * - the URL is on the chat-disabled list
 *
 * The AAO unified gate (`window.isA3CEnabled`) is intentionally NOT one of
 * them: it only withholds the everywhere default agent and must never hide a
 * page-provided legacy agent. See {@link isUnifiedA3CChatVetoed}.
 *
 * Consumed by EmbeddedChatManager (panel + config injection),
 * ConsolidatedSideNavInner (nav button) and useShowFocusHeaderAdsAgentToggle
 * (focus-header toggle) so all three surfaces cannot drift apart.
 */
export function isA3cChatDisabled(a3cConfig?: A3cChatGateConfig): boolean {
  return (
    a3cConfig?.loadBehavior === A3C_LOAD_BEHAVIOR_DISABLED ||
    isChatDisabledUrl(window.location.href)
  );
}

/**
 * Inputs to {@link resolveEffectiveA3cAgent}. Kept as a plain object so
 * callers can source each value from their own hooks/window inspection
 * without the helper reaching into React, DOM, or feature-flag SDKs itself.
 */
export interface ResolveEffectiveA3cAgentInputs {
  a3cConfig: A3cChatGateConfig | undefined;
  /**
   * True when the URL matches an EMBEDDED_MODE_URL rule
   * ({@link isEmbeddedModeUrl}). Forces the embedded resolution branch even
   * when the page has not stamped its own `embeddedChatConfig`, so the
   * everywhere default agent applies on those pages under the weblab.
   */
  isEmbeddedModeForcedByUrl: boolean;
  /**
   * True when the applicable A3C_EVERYWHERE weblab is enabled for this
   * surface AND the AAO unified gate (`window.isA3CEnabled`) has not vetoed
   * it. Callers select the right weblab for the page (T1/T2 elsewhere, UCB
   * on /cb* paths, UCM on /cm*), which mirrors EmbeddedChatManager's
   * path-aware wiring; useA3cEverywhereEnabled folds the gate in for the
   * panel and the sidenav button, useShowFocusHeaderAdsAgentToggle for the
   * focus header. The gate lives here, on the everywhere input, precisely so
   * it can only withhold the everywhere default — never a page-provided
   * agent.
   */
  isA3cEverywhereEnabled: boolean;
}

/**
 * Resolves the A3C agent identifier that will actually load on this page, or
 * `undefined` when no agent applies (chat hidden). Single source of truth
 * shared by:
 * - EmbeddedChatManager (panel visibility + config injection)
 * - ConsolidatedSideNavInner (sidenav Ads Agent button)
 * - useShowFocusHeaderAdsAgentToggle (focus-mode Ads Agent toggle)
 *
 * so the four surfaces cannot drift and a page-provided
 * `embeddedChatConfig: { selectedAgentIdentifier: '' }` correctly hides ALL
 * of them.
 *
 * Precedence (kept in lock-step with EmbeddedChatManager's original
 * implementation):
 * - Any page-level chat veto (loadBehavior DISABLED or a chat-disabled URL)
 *   returns undefined regardless of anything else.
 * - A page-provided embedded config's own agent always wins, INCLUDING an
 *   explicit empty string — that is the documented "chat hidden" signal.
 *   A null agent (JSON serializers emit null for unset fields) counts as
 *   unset and the everywhere default applies when the weblab is on.
 * - A URL forced into embedded mode without a page config resolves through
 *   the same embedded branch (no flyover agent fallback).
 * - Otherwise (flyover branch): the flyover agent wins when set; under the
 *   everywhere weblab an unset flyover agent falls back to the everywhere
 *   default; when the weblab is off, no agent applies.
 *
 * The AAO unified gate is NOT a veto here: it arrives folded into
 * `isA3cEverywhereEnabled`, so a vetoed page behaves exactly like one where
 * the everywhere weblab is off — the everywhere default is withheld, but a
 * page-provided (legacy) agent still loads.
 */
export function resolveEffectiveA3cAgent({
  a3cConfig,
  isEmbeddedModeForcedByUrl,
  isA3cEverywhereEnabled,
}: ResolveEffectiveA3cAgentInputs): string | undefined {
  if (isA3cChatDisabled(a3cConfig)) {
    return undefined;
  }
  const pageEmbeddedConfig = a3cConfig?.embeddedChatConfig;
  const pageAgentIdentifier = pageEmbeddedConfig?.selectedAgentIdentifier;
  const isEmbeddedConfigActive = !!pageEmbeddedConfig;
  if (isEmbeddedConfigActive || isEmbeddedModeForcedByUrl) {
    // Empty string is respected verbatim ('' -> undefined -> hidden).
    // Null / missing falls through to the everywhere default when the
    // weblab is on.
    return pageAgentIdentifier != null
      ? pageAgentIdentifier || undefined
      : isA3cEverywhereEnabled
        ? A3C_EVERYWHERE_DEFAULT_AGENT
        : undefined;
  }
  // Flyover branch: page did not opt into embedded mode and no URL rule
  // forces it.
  const flyoverAgent = a3cConfig?.flyoverChatConfig?.selectedAgentIdentifier;
  return isA3cEverywhereEnabled
    ? flyoverAgent || A3C_EVERYWHERE_DEFAULT_AGENT
    : undefined;
}
